import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

interface ControllerHoverValue {
  /** Bare UUID of the slicer the pointer is on (or whose popover is open), else null. */
  hoveredUuid: string | null;
  hover: (uuid: string) => void;
  /** Clears the hover only while `uuid` still owns it, so two tiles never fight over it. */
  unhover: (uuid: string) => void;
}

const ControllerHoverContext = createContext<ControllerHoverValue | null>(null);

const NO_PROVIDER: ControllerHoverValue = { hoveredUuid: null, hover: () => {}, unhover: () => {} };

/**
 * Which slicer the coach is pointing at. Kept out of DashboardContext so a hover does not
 * re-render every consumer of the dashboard state — only the tile frames that read it.
 */
export function ControllerHoverProvider({ children }: { children: ReactNode }) {
  const [hoveredUuid, setHoveredUuid] = useState<string | null>(null);
  const hover = useCallback((uuid: string) => setHoveredUuid(uuid), []);
  const unhover = useCallback(
    (uuid: string) => setHoveredUuid((current) => (current === uuid ? null : current)),
    [],
  );
  const value = useMemo(() => ({ hoveredUuid, hover, unhover }), [hoveredUuid, hover, unhover]);
  return <ControllerHoverContext.Provider value={value}>{children}</ControllerHoverContext.Provider>;
}

/** No-op outside a provider, so the slicer tile stays renderable anywhere. */
export function useControllerHover(): ControllerHoverValue {
  return useContext(ControllerHoverContext) ?? NO_PROVIDER;
}
